<template>
	<div class="container py-5">
        <h2>{{ $t('activityCharts') }}</h2>
        <nav aria-label="breadcrumb" class="mb-4">
            <ol class="breadcrumb">
                <li class="breadcrumb-item">
                    <RouterLink to="/modelings">
                        {{ $t('home') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">
                    <RouterLink :to="{ name: 'modeling', params: { id: this.$route.params.id } }">
                        {{ modelingName || $t('modelings') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">
                    <RouterLink :to="`/modeling/${this.$route.params.id}/modelingresult/${this.$route.params.modelingResultId}`">
                        {{ $t('modelingResult') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">{{ $t('activityCharts') }}</li>
            </ol>
        </nav>
		<div v-if="pending">
            <div class="d-flex justify-content-center loading-center">
                <div class="spinner-border" role="status">
                    <span class="sr-only">Loading...</span>
                </div>
            </div>
        </div>
        <div v-else>
            <div class="row mb-5" v-for="cfg in chartConfigs" :key="cfg.limitId" >
                <h5 class="mb-3">{{ $t('limit') }}: {{ cfg.limitName }}</h5>
                <div class="col-md-8">
                    <div v-if="getSelectedChartTotalWeight(cfg)" class="mb-1">
                        Total {{ getSelectedChartTotalWeight(cfg).weightAttributeName }}: {{ getSelectedChartTotalWeight(cfg).totalWeight }}
                    </div>
                    <apexchart
                        v-if="getSelectedChart(cfg)"
                        type="bar"
                        width="800"
                        height="500"
                        :options="getChartOptions(getSelectedChart(cfg), cfg)"
                        :series="getSelectedChart(cfg).series"
                        class="d-flex justify-content-center"
                    />
                </div>
                <div class="col-md-4">
                    <div class="row mb-3">
                        <label class="col-md-4">{{ $t('assetType') }}</label>
                        <select class="col-md-8 form-select" v-model.number="cfg.selectedAssetType" @change="onAssetTypeChanged(cfg)">
                            <option v-for="at in assetTypeList" :key="at.id" :value="at.id">
                                {{ at.name }}
                            </option>
                        </select>
                    </div>
                    <div class="row mb-3">
                        <label class="col-md-4">{{ $t('visualization') }}</label>
                        <select class="col-md-8 form-select" v-model="cfg.selectedVisualization" @change="getSelectedChart(cfg)">
                            <option value="" disabled selected>Select</option>
                            <option v-for="visualization in visualizationList?.filter(p => p.assetTypeId == cfg.selectedAssetType)" :key="visualization.value" :value="visualization.value">
                                {{ visualization.name }}
                            </option>
                        </select>
                    </div>
                    <div class="row mb-3">
                        <label class="col-md-4">{{ $t('areas') }}</label>
                        <select class="col-md-8 form-select" v-model="cfg.selectedGroupId" @change="cfg.selectedPackageId = null">
                            <option :value="null">{{ $t('allAreas') }}</option>
                            <option v-for="g in groups.filter(p => p.assetTypeId === cfg.selectedAssetType)" :key="g.value" :value="Number(g.value)">
                                {{ g.name }}
                            </option>
                        </select>
                    </div>
                    <div class="row mb-3">
                        <label class="col-md-4">{{ $t('packages') }}</label>
                        <select class="col-md-8 form-select" v-model="cfg.selectedPackageId" @change="cfg.selectedGroupId = null" >
                            <option :value="null">{{ $t('allPackages') }}</option>
                            <option v-for="p in packages.filter(x => x.assetTypeId === cfg.selectedAssetType)" :key="p.value" :value="Number(p.value)">
                                {{ p.name }}
                            </option>
                        </select>
                    </div>
                </div>
            </div>
            <div class="d-grid gap-2 d-md-flex mb-4">
                <button type="button" class="btn btn-secondary" @click="$router.push({ path: `/modeling/${$route.params.id}/modelingresult/${$route.params.modelingResultId}` })">
                {{ $t('back') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import AnalysisApi from "../../api/server/analysis";
import { CheckIfUserIsEditor } from "../../authCache";
import { formatAmount } from '../../utils/currencyUtils';

export default {
    name: "resultCharts",
    watch: {
        $route: "requestData"
    },
    async mounted() {
        await this.requestData();
    },
    data() {
        return {
            pending: true,
            isUserAdmin: false,
            limits: [],
            groups: [],
            packages: [], 
            assetTypeList: [],
            visualizationList: [],
            chartConfigs: [],
            modelingName: ""
        };
    },
    methods: {
        getSelectedChartTotalWeight(cfg) {
            if (cfg.selectedVisualization === 'cost') 
                return null;
            const chart = this.getSelectedChart(cfg);
            if (!chart || !chart.totalWeight || !chart.weightAttributeName)
                return null;
            return {
                totalWeight: chart.totalWeight,
                weightAttributeName: chart.weightAttributeName
            };
        },
        onAssetTypeChanged(cfg) {
            const firstVisForAt =
                this.visualizationList.find(v => v.assetTypeId === cfg.selectedAssetType)?.value || 'cost';

            cfg.selectedVisualization = firstVisForAt;
            cfg.selectedGroupId = null;
            cfg.selectedPackageId = null;
        },
        getSelectedChart(cfg) {
            const filteredCharts = cfg.charts.filter(
                c => c.assetTypeId === cfg.selectedAssetType && c.visualization === cfg.selectedVisualization
            );

            if (!filteredCharts.length) return null;

            // package chart
            if (cfg.selectedPackageId != null) {
                return filteredCharts.find(c => c.packageId === cfg.selectedPackageId) || null;
            }

            // group chart
            if (cfg.selectedGroupId != null) {
                return filteredCharts.find(c => c.groupId === cfg.selectedGroupId) || null;
            }

            // all chart (bez group i package)
            return filteredCharts.find(c => c.groupId == null && c.packageId == null) || null;
        },

        getChartOptions(chart, cfg) {
            return {
                chart: { type: "bar", height: 500 },
                title: { text: `Activity ${chart.visualizationName} for Limit: ${cfg.limitName}` },
                xaxis: {
                    categories: chart.years,
                    title: { text: "Year" },
                },
                yaxis: {
                    title: { text: chart.visualizationName },
                    labels: { formatter: (v) => formatAmount(v, chart.currency) },
                },
                tooltip: {
                    y: { formatter: (v) => formatAmount(v, chart.currency) },
                },
                plotOptions: {
                    bar: {
                        horizontal: false,
                        columnWidth: "50%",
                        endingShape: "rounded",
                    },
                },

                dataLabels: { enabled: false },
                legend: { position: "top" },
                colors: chart.series?.map(s => s.color)
            };
        },

        async requestData() {
            this.isUserAdmin = await CheckIfUserIsEditor();

            var analysisDetail = await AnalysisApi.analysisDetail(this.$route.params.id, this.$route.params.modelingResultId);
            if(analysisDetail.modelingInfo && analysisDetail.modelingInfo.selections) {
                this.modelingName = analysisDetail.modelingInfo.name;
            }

            var result = await AnalysisApi.activityGraphCharts(this.$route.params.id,this.$route.params.modelingResultId);

            this.assetTypeList = result.assetTypes;
            this.visualizationList = result.visualizationOptions;
            this.groups = result.groups || [];
            this.packages = result.packages || [];

            this.chartConfigs = result.limits.map((limitGroup) => ({
                limitId: limitGroup.limitId,
                limitName: limitGroup.limitName,
                charts: limitGroup.charts,
                selectedAssetType: result.assetTypes[0]?.id ?? null,
                selectedVisualization: result.visualizationOptions[0]?.value ?? null,
                selectedGroupId: null,
                selectedPackageId: null
            }));

            this.pending = false;
        }
    }
};
</script>
